
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(2, {
    message: 'Name must be at least 2 characters.',
  }).max(50),
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  phone: z.string().min(5, {
    message: 'Please enter a valid phone number.',
  }).optional(),
  message: z.string().min(10, {
    message: 'Message must be at least 10 characters.',
  }).max(500),
  business: z.string().default('').optional(),
});

export type FormData = z.infer<typeof formSchema>;

export const useContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);
  
  // Initialize the form with react-hook-form
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
      business: '',
    },
    mode: 'onChange', // Enable real-time validation
  });

  // Form submission handler
  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setSubmissionSuccess(false);
      
      // Insert data into Supabase contact_submissions table
      const { error } = await supabase
        .from('contact_submissions')
        .insert({
          name: data.name,
          email: data.email,
          phone: data.phone || null,
          message: data.message,
          business: data.business || 'Not specified',
        });
      
      if (error) {
        console.error('Error submitting form:', error);
        toast({
          title: "Something went wrong",
          description: error.message || "We couldn't send your message. Please try again later.",
          variant: "destructive",
        });
        return;
      }
      
      // Set success state and show success toast
      setSubmissionSuccess(true);
      toast({
        title: "Message received!",
        description: "We'll be in touch with you shortly.",
      });
      
      // Reset the form
      form.reset();
      
      // Reset success state after 3 seconds
      setTimeout(() => {
        setSubmissionSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Something went wrong",
        description: "We couldn't send your message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    submissionSuccess,
    onSubmit,
    formSchema,
  };
};
